import { ChangeEvent, ReactNode, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import IWButton from 'shared/components/thunderbolt/IWButton'
import IWGenericCard from 'shared/components/thunderbolt/IWGenericCard'
import IWTextInput from 'shared/components/thunderbolt/IWTextInput'
import { useToast } from 'shared/components/thunderbolt/IWToastContext'
import IWTypography from 'shared/components/thunderbolt/IWTypography'
import UserContext from 'shared/contexts/UserContext'
import { createOrgConfig } from 'shared/loadSchedulingClient'
import styled from 'styled-components'

const Wrapper = styled.div`
  display: flex;
  justify-content: left;
  flex-direction: column;
  gap: 1rem;
`

const StyledSectionRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: left;
  flex-direction: column;
  margin: 0 auto 0 0;

  & > * {
    flex: 0 0 calc(25% - 0.75rem); /* 25% width for 4 items, accounting for gap */
  }
`

const StyledFooter = styled.footer`
  display: flex;
  align-items: center;
  justify-content: left;
  gap: 0.5rem;
`

const PreferencesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const CardContent = styled.div`
  display: flex;
  padding: 1rem;
  flex-direction: column;
  gap: 1rem;
`

const StyledInfoBlock = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  gap: 0.5rem;
`

const StyledInfoSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`

const Section = ({
  title,
  children,
}: {
  title?: string
  children: ReactNode
}) => {
  return (
    <PreferencesSection>
      {title && (
        <IWTypography size="lg" weight="medium">
          {title}
        </IWTypography>
      )}
      <IWGenericCard>
        <CardContent>
          <StyledInfoSection>
            <StyledInfoBlock>{children}</StyledInfoBlock>
          </StyledInfoSection>
        </CardContent>
      </IWGenericCard>
    </PreferencesSection>
  )
}

export default function () {
  const navigate = useNavigate()
  const toast = useToast()
  const { t } = useTranslation()
  const { organization } = useContext(UserContext)
  const [market, setMarket] = useState('')
  const [brand, setBrand] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const hasMarketError = () => market.length < 3 || market.length > 10
  const hasBrandError = () => brand.length < 1 || brand.length > 20

  const handleNavigateBack = () => {
    navigate('/admin/load-scheduling/configs')
  }

  const handleCreate = async () => {
    try {
      setIsLoading(true)

      await createOrgConfig({ market, brand, org: organization })

      setIsLoading(false)

      handleNavigateBack()
    } catch (error) {
      toast.alert(error?.response?.data, {
        canDismiss: true,
        duration: 10_000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <main>
      <Wrapper>
        <Section title={t('loadScheduling.labels.configuration')}>
          <StyledSectionRow>
            <IWTextInput
              label={t('loadScheduling.labels.market')}
              maxLength={20}
              required
              hasError={hasMarketError()}
              value={market}
              onKeyUp={(e) => {
                if (hasMarketError() || hasBrandError()) {
                  e.preventDefault()
                  return
                }

                if (e.key === 'Enter') {
                  handleCreate()
                }
              }}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setMarket(e.target.value)
              }}
            />

            <IWTextInput
              label={t('loadScheduling.labels.brand')}
              maxLength={20}
              required
              value={brand}
              hasError={hasBrandError()}
              onKeyUp={(e) => {
                if (hasMarketError() || hasBrandError()) {
                  e.preventDefault()
                  return
                }

                if (e.key === 'Enter') {
                  handleCreate()
                }
              }}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setBrand(e.target.value)
              }}
            />
          </StyledSectionRow>
        </Section>

        <StyledFooter>
          <IWButton color="grey" variant="outline" onClick={handleNavigateBack}>
            {t('button.cancel')}
          </IWButton>

          <IWButton
            onClick={handleCreate}
            disabled={hasMarketError() || hasBrandError() || isLoading}
          >
            {t('button.create')}
          </IWButton>
        </StyledFooter>
      </Wrapper>
    </main>
  )
}
